import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, Input, Label } from "reactstrap";

import { Btn, H4 } from "../../../../AbstractElements";
import { STATUS_200 } from "../../../../Api/constants";
import FixedFooter from "../../../../CommonElements/FixedFooter";
import StepperHorizontal from "../../../../CommonElements/StepperHorizontal";
import LearningPlanContainer from "../../../../container/UserGroupLearningPlanContainer";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  createUserGroup,
  getUserGroupById,
  updateUserGroup,
} from "../../../../ReduxToolkit/Reducers/UserGroupSlice";
import { getDownloadableUrl } from "../../../../ReduxToolkit/Reducers/UserSlice";
import {
  AutoAssignLearningPlan,
  Back,
  CreateUserGroups,
  Done,
  EditUserGroups,
  GroupNameRequired,
  Next,
  PleaseAddLearningPlansToGroup,
  PleaseAddSomeUsersToGroup,
  PleaseProvideRequiredInformation,
} from "../../../../utils/Constant";
import {
  formatDate,
  groupCardStepper,
  scrollToTop,
  showToast,
} from "../../../../utils/helper/helper";

import AddUser from "./AddUser";
import BasicDetails from "./BasicDetails";

const AddGroup = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [formValues, setFormValues] = useState({
    groupName: "",
    description: "",
  });
  const { userGroupById } = useAppSelector(state => state.userGroup);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [stepperData, setStepperData] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]);
  const [addedUsersCards, setAddedUsersCards] = useState([]);
  const [addedLearningPlans, setAddedLearningPlans] = useState([]);
  const [userCardDetail, setUserCardDetail] = useState([]);
  const [isAutoAssign, setIsAutoAssign] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");
  const page = searchParams.get("page");
  const id = searchParams.get("id");
  const totalSteps = stepperData.length;

  useEffect(() => {
    if (id) {
      dispatch(getUserGroupById({ id: id }));
    }
  }, [id]);

  useEffect(() => {
    if (userGroupById) {
      if (type === "basic") {
        setFormValues({
          groupName: userGroupById?.name,
          description: userGroupById?.description,
        });
      }

      if (type === "user") {
        setAddedUsers(userGroupById?.users);
      }
    }
  }, [userGroupById]);

  useEffect(() => {
    let filteredStepper = groupCardStepper;

    if (type === "basic") {
      filteredStepper = groupCardStepper.filter(
        step => step === "Basic details"
      );
    } else if (type === "user") {
      filteredStepper = groupCardStepper.filter(step => step === "Add User");
    }

    setStepperData(filteredStepper);
  }, [type]);

  useEffect(() => {
    dispatch(getDownloadableUrl());
  }, []);

  const handleNext = () => {
    if ((type === "basic" || !type || type === "") && validateForm()) {
      return;
    }

    if (activeStep < totalSteps) {
      setActiveStep(prevStep => prevStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep(prevStep => prevStep - 1);
    }
  };

  const handleSubmit = async () => {
    let res = null;
    if (type === "basic") {
      res = await dispatch(
        updateUserGroup({
          type: "edit_group_details",
          payload: {
            ...userGroupById,
            name: formValues?.groupName,
            description: formValues?.description,
          },
        })
      );
    }

    if (type === "user") {
      if (addedUsersCards?.length === 0) {
        showToast(PleaseAddSomeUsersToGroup, "error");
        return;
      }
      if (addedLearningPlans?.length === 0 && isAutoAssign) {
        showToast(PleaseAddLearningPlansToGroup, "error");
        return;
      }
      res = await dispatch(
        updateUserGroup({
          payload: {
            ...userGroupById,
            auto_assign_plans: addedLearningPlans?.length > 0,
            plans: isAutoAssign
              ? userCardDetail
                  ?.filter(detail =>
                    addedLearningPlans?.includes(Number(detail?.id))
                  )
                  ?.map(item => {
                    return {
                      group_id: id,
                      plan_id: Number(item?.id),
                      start_date: formatDate(item?.start_date),
                      end_date: formatDate(item?.end_date),
                    };
                  }) || []
              : [],
            users: addedUsers || [],
          },
          type: "edit_user_group",
        })
      );
    }

    if (!type || type === "") {
      if (addedUsers?.length === 0) {
        showToast(PleaseAddSomeUsersToGroup, "error");
        return;
      }

      res = await dispatch(
        createUserGroup({
          payload: {
            name: formValues?.groupName,
            description: formValues?.description,
            users: addedUsers || [],
          },
        })
      );
    }

    if (
      res?.payload?.status_code === STATUS_200 &&
      (page === "home" || page === "" || page === null)
    ) {
      navigate("/user-management/user-groups");
    }

    if (res?.payload?.status_code === STATUS_200 && page === "details") {
      navigate(`/user-management/group-details?group_id=${id}`);
    }
  };

  const handleChange = e => {
    setFormValues(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const validateForm = () => {
    const newErrors = {
      groupName: "",
      description: "",
    };

    let isError = false;
    if (!formValues?.groupName) {
      newErrors.groupName = GroupNameRequired;
      isError = true;
    }

    setErrors(newErrors);
    if (isError) {
      showToast(PleaseProvideRequiredInformation, "error");
      scrollToTop();
    }
    return isError;
  };

  const handleAutoAssign = () => {
    setIsAutoAssign(prevState => !prevState);
  };

  return (
    <div className="page-body page-body-margin add-group pb-5">
      <Card>
        <CardHeader>
          <H4>{id ? EditUserGroups : CreateUserGroups}</H4>
        </CardHeader>
        <CardBody>
          <div className="basic-wizard important-stepperData">
            {!type && (
              <StepperHorizontal data={stepperData} level={activeStep} />
            )}
            {stepperData[activeStep - 1] === groupCardStepper[0] && (
              <BasicDetails
                formValues={formValues}
                errors={errors}
                handleInputChange={handleChange}
              />
            )}
            {stepperData[activeStep - 1] === groupCardStepper[1] && (
              <AddUser
                addedUsers={addedUsers}
                setAddedUsers={setAddedUsers}
                setAddedUsersCards={setAddedUsersCards}
              />
            )}

            {type === "user" && (
              <div className="form-check text-dark">
                <Input
                  className="primary-checkbox"
                  id="flexCheckPublishLater"
                  type="checkbox"
                  checked={isAutoAssign}
                  onChange={handleAutoAssign}
                />
                <Label
                  className="text-dark"
                  htmlFor="flexCheckPublishLater"
                  check
                >
                  {AutoAssignLearningPlan}
                </Label>
              </div>
            )}

            {isAutoAssign && (
              <LearningPlanContainer
                learningPlanId={id}
                addedLearningPlans={addedLearningPlans}
                setAddedLearningPlans={setAddedLearningPlans}
                userCardDetail={userCardDetail}
                setUserCardDetail={setUserCardDetail}
              />
            )}
          </div>
        </CardBody>
      </Card>

      <FixedFooter>
        {activeStep > 1 && (
          <Btn
            outline
            className="alert-light-primary"
            color="primary"
            onClick={handleBack}
          >
            {Back}
          </Btn>
        )}
        <Btn color="primary" onClick={handleNext}>
          {activeStep === totalSteps ? Done : Next}
        </Btn>
      </FixedFooter>
    </div>
  );
};

export default AddGroup;
